import React, { useCallback, useContext, useState } from "react";
import { Tabs } from "antd";
import { useNavigate, useSearchParams } from "react-router-dom";
import dayjs from "dayjs";
import { ListScreen } from "../common/ListScreen";
import { HeroBand } from "../Dashboard/BoardParts";
import { boardTheme } from "../Dashboard/heroTheme";
import CgIcon from "../common/CgIcon";
import { UserContext } from "../../../context/UserContext";
import { useOrgFacilities } from "../common/useOrgFacilities";
import StockOutTable from "./StockOutTable";
import NearExpiryTable from "./NearExpiryTable";
import NonMovingStockTable from "./NonMovingStockTable";

const TAB_KEYS = ["stock-out", "near-expiry", "non-moving-stock"];

const StockOut = () => {
  const navigate = useNavigate();
  const { userDetails } = useContext(UserContext);
  const { currentFacilityName } = useOrgFacilities();

  /*
   * The tab lives in the URL so the dashboard's Near Expiry figure can link
   * straight to it, and so a tab someone is on survives a reload or a shared
   * link. An unrecognised value falls back to the first tab.
   */
  const [searchParams, setSearchParams] = useSearchParams();
  const requested = searchParams.get("tab");
  const activeKey = TAB_KEYS.includes(requested) ? requested : TAB_KEYS[0];

  const [stock, setStock] = useState(null);

  /*
   * The band's figures come off whichever list the open tab has just fetched.
   *
   * Every one of these endpoints answers with a `tab_counts` block alongside
   * its own page - out of stock, low stock, near expiry, non-moving - each
   * counted over the whole set rather than over the rows that came back. So
   * the tab that is open already holds all four, and the screen reads them
   * from it instead of fetching for itself.
   *
   * It used to fetch two lists of its own on mount, which meant landing on
   * Stock Out cost three calls: this screen asking for the stock-out list and
   * the near-expiry list, and the table under it asking for the stock-out
   * list all over again. Two of the three were for figures already on their
   * way, and one was for a tab nobody had opened.
   */
  const readTabCounts = useCallback((payload) => {
    const tabs = payload?.tab_counts;
    if (!tabs) return;

    /* The one medicine expiring soonest, where the open tab happens to be the
       one that lists them. No count can carry it, and it is not worth a call
       of its own on a screen that is not showing expiries. */
    const soonest = [...(payload.results || [])]
      .filter((row) => row.expiry)
      .sort((a, b) => dayjs(a.expiry).valueOf() - dayjs(b.expiry).valueOf())[0];

    setStock((prev) => ({
      out: Number(tabs.stock_out) || 0,
      low: Number(tabs.low_stock) || 0,
      nearExpiry: Number(tabs.near_expiry) || 0,
      nonMoving: Number(tabs.non_moving_stock) || 0,
      toOrder: Number(tabs.stock_out) || 0,
      /* Kept from the last tab that knew it, so switching away from Near
         Expiry does not blank the line underneath the headline. */
      soonest: soonest || prev?.soonest || null,
    }));
  }, []);

  /*
   * Only the open tab is mounted, and only the open tab fetches. antd renders
   * a pane when it is first opened and `destroyInactiveTabPane` takes it down
   * again on the way out, so nothing behind the scenes is asking for lists
   * nobody is looking at.
   */
  const items = [
    {
      key: "stock-out",
      label: "Stock Out",
      children: <StockOutTable onLoaded={readTabCounts} />,
    },
    {
      key: "near-expiry",
      label: "Near Expiry",
      children: <NearExpiryTable onLoaded={readTabCounts} />,
    },
    {
      key: "non-moving-stock",
      label: "Non-Moving Stock",
      children: <NonMovingStockTable onLoaded={readTabCounts} />,
    },
  ];

  const openTab = (key) =>
    setSearchParams(key === TAB_KEYS[0] ? {} : { tab: key }, { replace: true });

  const toOrder = stock?.toOrder ?? 0;
  const soonest = stock?.soonest;
  const daysLeft = soonest ? dayjs(soonest.expiry).diff(dayjs(), "day") : null;

  const subLine = [
    stock?.out ? `${stock.out} already out of stock` : null,
    soonest
      ? `${soonest.pharmacy_medicine_name} expires in ${Math.max(daysLeft, 0)} days`
      : null,
  ]
    .filter(Boolean)
    .join(" · ");

  return (
    <ListScreen>
      <div style={boardTheme(userDetails?.activeOrg?.color)}>
        <HeroBand
          kicker={`Inventory${
            currentFacilityName ? ` · ${currentFacilityName}` : ""
          }`}
          headline={
            toOrder
              ? `${toOrder} ${
                  toOrder === 1 ? "medicine needs" : "medicines need"
                } ordering`
              : "Nothing needs ordering"
          }
          sub={subLine || "Nothing is running low or close to expiry"}
          /* The two ways to actually restock, and nothing else. */
          actions={[
            {
              label: "Raise Purchase Order",
              icon: <CgIcon name="clipcheck" size={16} stroke={2} />,
              solid: true,
              onClick: () => navigate("/pharmacy/purchase-order/new"),
            },
            {
              label: "Raise Purchase Entry",
              icon: <CgIcon name="truck" size={16} stroke={2} />,
              onClick: () => navigate("/pharmacy/purchase-entry/new"),
            },
          ]}
          /*
           * Read-only here. The lists these count are already on this screen,
           * a tab away - it is the dashboard's figures that need to take you
           * somewhere.
           */
          /* One figure per tab on the screen, and a fourth for the stock that
             has simply stopped moving - which is the thing the money figure
             that used to sit here never actually named. */
          metrics={[
            { label: "Out of Stock", value: stock?.out ?? 0 },
            { label: "Low Stock", value: stock?.low ?? 0 },
            { label: "Near Expiry", value: stock?.nearExpiry ?? 0 },
            { label: "Non-Moving Stock", value: stock?.nonMoving ?? 0 },
          ]}
        />
      </div>

      <Tabs
        items={items}
        destroyInactiveTabPane
        activeKey={activeKey}
        // replace: switching tabs should not stack up browser history entries
        onChange={openTab}
      />
    </ListScreen>
  );
};

export default StockOut;
